'use client';

import Icon from '@shared/components/Icon/Icon';
import {
    TypeButtonLinkComponentSkeleton,
    TypeMegaMenuComponentWithoutUnresolvableLinksResponse,
} from '@shared/features/contentful/_generated';
import { Entry } from 'contentful';
import styles from './Header.module.scss';
import HeaderLink from './HeaderLink';
import { Suspense } from 'react';
import HeaderLinkFallback from './HeaderLinkFallback';
import { HeaderWrapper } from '@lp/components/Header/HeaderWrapper';
import { useSettingsContext } from '@shared/contexts/SettingsContext';
import { LightRetouchButtonVariant } from '@shared/light-retouch';

interface HeaderProps {
    data: TypeMegaMenuComponentWithoutUnresolvableLinksResponse;
}

export default function Header({ data }: HeaderProps) {
    const { getLabel } = useSettingsContext();

    const skipToContentLabel = getLabel(['lpheader', 'skipToContent']);

    return (
        <HeaderWrapper>
            <a href="#main-content" className="skip-to-content-link" tabIndex={0}>
                {skipToContentLabel}
            </a>

            <div className={styles.container}>
                <div className={styles.logo} data-testid="header-logo">
                    <Icon
                        type="ppLogoMobile"
                        width={144}
                        height={20}
                        className="md:hidden"
                        aria-hidden="true"
                    />
                    <Icon
                        type="ppLogoNew"
                        width={202}
                        height={28}
                        className="hidden md:block"
                        aria-hidden="true"
                    />
                </div>
                <div aria-hidden="true" className={styles.divider} />
                <nav className={styles['links-container']}>
                    <ul className={styles['links-block']}>
                        {data.fields.items?.filter(Boolean).map((item, index) => {
                            const buttonLinkItem = item as Entry<
                                TypeButtonLinkComponentSkeleton,
                                'WITHOUT_UNRESOLVABLE_LINKS',
                                string
                            >;
                            const variant =
                                index === 0
                                    ? LightRetouchButtonVariant.Primary
                                    : LightRetouchButtonVariant.Secondary;

                            return (
                                <li key={buttonLinkItem.sys.id}>
                                    <Suspense
                                        fallback={
                                            <HeaderLinkFallback
                                                linkItem={buttonLinkItem}
                                                variant={variant}
                                            />
                                        }
                                    >
                                        <HeaderLink linkItem={buttonLinkItem} variant={variant} />
                                    </Suspense>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </div>
        </HeaderWrapper>
    );
}
